import { BinaryWriter } from "../binary.js";
import { ClientOp, ServerOp } from "../opcodes.js";
/**
 * Contêiner de destino/origem `0` é o equipamento do personagem, e `slot` é o
 * `ItemSlot`. Qualquer outro valor é o id de instância de um contêiner.
 */
export const CONTAINER_EQUIPMENT = 0;
/** Contêiner virtual do chão. Qual tile, ver `MoveItemPacket.fromX`. */
export const CONTAINER_GROUND = 0xffffffff;
/**
 * "O tile onde eu estou" nos campos de coordenada de `MoveItem`.
 *
 * Sentinela e não `0`, porque `(0, 0)` é um tile como qualquer outro. `0xffff` está fora de
 * qualquer mapa que o protocolo consiga endereçar — a largura viaja como `u16` no Welcome, e
 * uma região de 65.535 tiles de lado seriam 4 bilhões de tiles num `Uint8Array`.
 *
 * Existe para que o gesto antigo continue dizendo o que sempre disse: `CONTAINER_GROUND` sem
 * coordenada é o chão sob os pés, que era a única coisa que o pacote sabia endereçar antes de
 * o arraste no cenário existir.
 */
export const GROUND_HERE = 0xffff;
/** Bytes por item no pacote: parent(4) + slot(1) + id(4) + type(2) + count(2). */
export const INVENTORY_ENTRY_SIZE = 13;
export function encodeInventory(packet, writer = new BinaryWriter(64 + packet.entries.length * INVENTORY_ENTRY_SIZE)) {
    writer.u8(ServerOp.Inventory).u16(packet.entries.length);
    for (const entry of packet.entries) {
        writer
            .u32(entry.parentId)
            .u8(entry.slot)
            .u32(entry.id)
            .u16(entry.typeId)
            .u16(entry.count);
    }
    return writer;
}
export function decodeInventory(reader) {
    const count = reader.u16();
    const entries = new Array(count);
    for (let i = 0; i < count; i++) {
        entries[i] = {
            parentId: reader.u32(),
            slot: reader.u8(),
            id: reader.u32(),
            typeId: reader.u16(),
            count: reader.u16(),
        };
    }
    return { entries };
}
export function encodeMoveItem(packet, writer = new BinaryWriter(24)) {
    writer
        .u8(ClientOp.MoveItem)
        .u32(packet.fromContainer)
        .u8(packet.fromSlot)
        .u32(packet.toContainer)
        .u8(packet.toSlot)
        .u16(packet.count)
        .u16(packet.fromX ?? GROUND_HERE)
        .u16(packet.fromY ?? GROUND_HERE)
        .u16(packet.toX ?? GROUND_HERE)
        .u16(packet.toY ?? GROUND_HERE);
    return writer;
}
export function decodeMoveItem(reader) {
    const packet = {
        fromContainer: reader.u32(),
        fromSlot: reader.u8(),
        toContainer: reader.u32(),
        toSlot: reader.u8(),
        count: reader.u16(),
    };
    const fromX = reader.u16();
    const fromY = reader.u16();
    const toX = reader.u16();
    const toY = reader.u16();
    // Os campos ficam **fora** do objeto quando são sentinela, em vez de entrar com `0xffff`
    // dentro: assim quem lê o pacote no servidor escreve `packet.fromX ?? player.x` e não
    // precisa conhecer a sentinela. Ela morre aqui, que é onde nasceu.
    if (fromX !== GROUND_HERE && fromY !== GROUND_HERE) {
        packet.fromX = fromX;
        packet.fromY = fromY;
    }
    if (toX !== GROUND_HERE && toY !== GROUND_HERE) {
        packet.toX = toX;
        packet.toY = toY;
    }
    return packet;
}
export function encodeUseItem(packet, writer = new BinaryWriter(12)) {
    writer
        .u8(ClientOp.UseItem)
        .u32(packet.container)
        .u8(packet.slot)
        .u16(packet.x ?? GROUND_HERE)
        .u16(packet.y ?? GROUND_HERE);
    return writer;
}
export function decodeUseItem(reader) {
    const packet = { container: reader.u32(), slot: reader.u8() };
    const x = reader.u16();
    const y = reader.u16();
    // A sentinela morre aqui, como em `decodeMoveItem`: quem lê o pacote no servidor escreve
    // `packet.x ?? player.x` e não precisa conhecê-la.
    if (x !== GROUND_HERE && y !== GROUND_HERE) {
        packet.x = x;
        packet.y = y;
    }
    return packet;
}
export function encodeUseOn(packet, writer = new BinaryWriter(16)) {
    writer
        .u8(ClientOp.UseOn)
        .u32(packet.container)
        .u8(packet.slot)
        .u32(packet.targetId)
        .u16(packet.x)
        .u16(packet.y);
    return writer;
}
export function decodeUseOn(reader) {
    return {
        container: reader.u32(),
        slot: reader.u8(),
        targetId: reader.u32(),
        x: reader.u16(),
        y: reader.u16(),
    };
}
//# sourceMappingURL=inventory.js.map